import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { withApollo, compose, graphql } from 'react-apollo';
import get from 'lodash/get';
import forEach from 'lodash/forEach';
import { withCourseOutline } from 'chameleon-packages/units';
import { getFirstUnit, getUnitByNumber } from 'chameleon-packages/units/utils';
import Header from 'chameleon/ui-stack/ui/page/header';
import Body from 'chameleon/ui-stack/ui/page/body';
import useNavigation from 'chameleon/ui-stack/hooks/useNavigation';
import HorizontalTabs from 'chameleon/ui-stack/ui/base/tabs/horizontal-tabs';
import SubunitsList from 'chameleon/ui-stack/ui/units/subunits-list';
import SubunitTypes from 'chameleon/ui-stack/ui/units/subunit-types';
import ExpandButton from 'chameleon/ui-stack/ui/units/expand-button';
import HorizontalTabsContainer from 'chameleon/ui-stack/ui/page/horizontal-tabs-container';
import useLastAccessedUnit from 'chameleon/ui-stack/hooks/use-last-accessed-unit';
import TodoCards from 'chameleon-src/components/TodoCards';
import VideoResourcePreviewWrapper from 'chameleon-src/components/VideoResourcePreviewWrapper';
import UnitResourcesContainer from 'chameleon-packages/units/components/UnitResourcesContainer';
import useFilterCoTabs from 'chameleon-packages/units/hooks/useFilterCoTabs';
import useValidateResourceType from 'chameleon-packages/units/hooks/useValidateResourceType';
import useGetAssignments from 'chameleon/ui-stack/hooks/use-assignments-fetch/unit-outline';
import AssignHelper from 'legacy-packages/unit-outline/src/components/_shared/AssignHelper';
import ShareHelper from 'legacy-packages/unit-outline/src/components/_shared/ShareHelper';
import IsLdbEnabled from 'chameleon-src/utility/isLdbEnabled';
import IsMobileDevice from 'legacy-src/utility/isMobile';
import { logSubjectVisitMutation } from 'legacy-src/queries';
import { checkWizardOnLoadAndRedirect as checkWizardOnLoadAndRedirectAction } from 'legacy-packages/wizards/src/actions';
import { LC_CONSTANTS } from 'legacy/src/components/impersonating/utils/constants';
import { isImpersonating } from 'legacy/src/components/impersonating/utils';
import TopicQuestionPreviewWrapper from 'chameleon-src/components/TopicQuestionPreviewWrapper';
import ResourceSearchFauxdal from 'chameleon-src/components/ResourceSearch/Fauxdal';
import PageTitle from 'legacy/src/components/PageTitle';
import QuestionPreviewWrapper from 'chameleon-src/components/QuestionPreviewWrapper';
import { COURSE_GUIDE_LABEL } from 'chameleon-src/components/SidebarWrapperTwoLevels/constants';
import { RESOURCE_TYPE_KEYS } from 'chameleon/ui-stack/ui/units/subunit-types/SubunitTypes';
import SearchControl from '../ResourceSearch/SearchControl';
import { PREAP_FILTERS } from '../ResourceSearch/utils/constants';

const HomeContainer = (props) => {
  const {
    router,
    courseOutline,
    userData,
    subjectData,
    visitedUnits,
    params,
    client,
    featureFlag,
    checkWizardOnLoadAndRedirect,
    isImpersonating,
    logSubjectVisit,
    location: { pathname },
    dynamicItemsByUnit,
  } = props;
  const {
    me: { initId },
    isStudent,
  } = userData;
  const {
    cb_session_expiration_override: cbSessionExpirationOverride,
    cb_session_expiration_check_before_take_assignment: checkCBSessionFlag,
    show_ap_stories_in_user_menu: showAPStoriesInUserMenu,
    levity_use_createtime: levityUseCreateTime,
    outline_search_instructional_resource_search_text:
      outlineSearchInstructionalResourceSearchText,
    secure_browser: secureBrowser,
    secure_browser_preap: secureBrowserPreAp,
    assign_all_videos: assignAllVideos,
    student_practice_problems,
    spp_filter_by_subject,
    spp_filter_by_school,
    bluebook_ui: blueBookUiFlag,
  } = featureFlag;
  const studentPracticeProblems =
    student_practice_problems ||
    (spp_filter_by_subject && spp_filter_by_school);
  const { subject: subjectId } = router.params;
  const { isPreApSubject, name: subjectName } = subjectData;

  const [pageTitle, setPageTitle] = useState(null);

  useEffect(() => {
    const re = new RegExp(`/${subjectId}/.*`);
    if (pathname.match(re)) {
      !isImpersonating && logSubjectVisit(pathname);
    }
  }, [pathname, subjectId]);

  useEffect(() => {
    if (isStudent && showAPStoriesInUserMenu) {
      // This checks if the user has seen the Welcome Videos and redirects them to it if not.
      checkWizardOnLoadAndRedirect(props);
    }
  }, [showAPStoriesInUserMenu]);

  const { pushParamsToHistory, queryParams, isHomePage } = useNavigation({
    router,
  });

  const [searchTerm, setSearchTerm] = useState(queryParams.searchTerm || '');

  const { type: resourceType = '', unit, mode = '' } = queryParams || {};
  const isSearchMode = mode.trim() === 'search';
  const showResourceSearch = !isStudent;

  useEffect(() => {
    if (subjectName) {
      setPageTitle(`${subjectName} Unit ${unit || 0} ${COURSE_GUIDE_LABEL}`);
    }
  }, [unit, subjectName]);

  const getDynamicItems = (dynamicItemsByUnit, currentUnitNumber) => {
    let dynamicItems = [];
    forEach(dynamicItemsByUnit[currentUnitNumber] || {}, (items) => {
      dynamicItems.push(...items);
    });

    return dynamicItems;
  };

  const { teacherAssignments, loadingAssignments } = useGetAssignments({
    client,
    router,
    userData,
  });

  const currentUnitNumber = useLastAccessedUnit({
    visitedUnits,
    subjectId,
    initId,
    unit,
  });

  const AP_FILTERS = [
    {
      name: 'All',
      value: '',
      values: ['videos', 'topic-questions', 'progress-checks', 'more'],
    },
    {
      name: 'Videos',
      value: 'videos',
    },
    {
      name: 'Topic Questions',
      value: 'topic-questions',
    },
    {
      name: 'Sources',
      value: 'sources',
    },
    {
      name: 'Progress Checks',
      value: 'progress-checks',
    },
    {
      name: 'Practice Exams',
      value: 'practice-exams',
    },
    ...(studentPracticeProblems
      ? [
          {
            name: 'Student Practice',
            value: 'practice-problems',
          },
        ]
      : []),
    {
      name: 'More',
      value: 'more',
    },
  ];

  let typeFilters = !isPreApSubject ? AP_FILTERS : PREAP_FILTERS;

  [typeFilters] = useFilterCoTabs({
    isHomePage,
    unitNumber: currentUnitNumber,
    units: get(courseOutline, 'units', []),
    typeFilters,
    studentPracticeProblems,
    isPreApSubject,
  });

  if (isPreApSubject && parseInt(currentUnitNumber, 10) === 0) {
    typeFilters = [
      { name: 'All', value: '' },
      { name: 'Resources', value: 'more' },
    ];
  }

  useValidateResourceType({ resourceType, typeFilters, router });

  const hoverItems = useMemo(
    () => getDynamicItems(dynamicItemsByUnit, currentUnitNumber),
    [dynamicItemsByUnit, currentUnitNumber],
  );

  const {
    loading,
    units = [],
    unitsLabel,
    widgetTagColumn,
    widgetTagColumnLabel,
    subunitLabel,
  } = courseOutline || {};
  if (loading) {
    return null;
  }

  const currentUnit =
    getUnitByNumber(currentUnitNumber, units) || getFirstUnit(units) || {};
  const { displayName, title, subunits, examWeighting } = currentUnit;
  const courseOutlineConfig = {
    widgetTagColumn,
    widgetTagColumnLabel,
  };

  const isLdbEnabled = IsLdbEnabled(
    secureBrowser,
    secureBrowserPreAp,
    isPreApSubject,
  );
  const isMobileDevice = IsMobileDevice();

  const { previewId } = params;

  return (
    <Fragment>
      {pageTitle && <PageTitle titleString={pageTitle} />}
      {previewId && (
        <TopicQuestionPreviewWrapper
          blueBookUiFlag={blueBookUiFlag}
          userData={userData}
        />
      )}
      <QuestionPreviewWrapper
        key={`question-preview-wrapper-${currentUnitNumber}-${subjectId}`}
        items={hoverItems}
        hoveredItemParent=".hover-question-parent"
        questionsContainer={`[data-type='${RESOURCE_TYPE_KEYS['topic-questions']}']`}
      />
      {showResourceSearch && isSearchMode && (
        <ResourceSearchFauxdal
          router={router}
          typeFilters={typeFilters}
          pushParamsToHistory={pushParamsToHistory}
          queryParams={queryParams}
          isStudent={isStudent}
          outlineSearchInstructionalResourceSearchText={
            outlineSearchInstructionalResourceSearchText
          }
          educationPeriod={(courseOutline || {}).educationPeriod}
          unitNumber={currentUnitNumber}
          units={units}
          isPreApSubject={isPreApSubject}
          widgetTagColumn={widgetTagColumn}
          widgetTagColumnLabel={widgetTagColumnLabel}
          unitsLabel={unitsLabel}
          teacherAssignments={teacherAssignments}
          loadingAssignments={loadingAssignments}
          courseOutline={courseOutline}
          subunitLabel={subunitLabel}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
        />
      )}
      <VideoResourcePreviewWrapper currentUnit={currentUnit} />
      {isStudent && (
        <TodoCards
          isLdbEnabled={isLdbEnabled}
          params={params}
          userData={userData}
          subjectData={subjectData}
          router={router}
          checkCBSessionFlag={checkCBSessionFlag}
          cbSessionExpirationOverride={cbSessionExpirationOverride}
          levityUseCreateTime={levityUseCreateTime}
          isMobileDevice={isMobileDevice}
          showTimedAssignmentWarning
        />
      )}
      <Header subtitle={COURSE_GUIDE_LABEL} title={displayName}>
        {title && (
          <div className="text-[18px] leading-[22px] font-medium text-black mt-[10px] mb-[8px] self-center max-w-600">
            {title}
          </div>
        )}
        {isStudent && examWeighting && (
          <div className="mt-8">AP Exam Weighting {examWeighting}</div>
        )}
        {showResourceSearch && (
          <div className="flex w-256 self-center mt-[16px]">
            <SearchControl
              initSearch
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
              pushParamsToHistory={pushParamsToHistory}
              isStudent={isStudent}
              outlineSearchInstructionalResourceSearchText={
                outlineSearchInstructionalResourceSearchText
              }
              onSearch={(text) =>
                pushParamsToHistory({
                  mode: 'search',
                  searchTerm: text,
                })
              }
            />
          </div>
        )}
      </Header>
      {!isStudent && (
        <UnitResourcesContainer
          unitData={currentUnit}
          unitsLabel={unitsLabel}
          widgetTagColumn={widgetTagColumn}
          subjectId={subjectId}
        />
      )}
      <HorizontalTabsContainer
        leftContainer={
          isStudent ? null : (
            <HorizontalTabs
              tabs={typeFilters.map((filter) => ({
                name: filter.name,
                onClick: () => pushParamsToHistory({ type: filter.value }),
                isActive: filter.value === resourceType,
              }))}
            />
          )
        }
        rightContainer={
          (!isSearchMode && !resourceType && (
            <ExpandButton
              unitNumber={currentUnitNumber}
              subunits={subunits || []}
              subjectId={subjectId}
            />
          )) ||
          null
        }
      />
      {(units.length &&
        ((!resourceType && (
          <Body>
            <SubunitsList
              key={`subunitslist-${subjectId}`}
              params={params}
              courseOutlineConfig={courseOutlineConfig}
              unit={currentUnit}
              teacherAssignments={teacherAssignments}
              loadingAssignments={loadingAssignments}
              subunitLabel={subunitLabel}
              subjectId={subjectId}
            />
          </Body>
        )) || (
          <SubunitTypes
            resourceType={resourceType}
            unit={currentUnit}
            subunits={subunits}
            widgetTagColumn={widgetTagColumn}
            unitsLabel={unitsLabel}
            subjectData={subjectData}
            teacherAssignments={teacherAssignments}
            loadingAssignments={loadingAssignments}
            assignAllVideos={assignAllVideos}
            subunitLabel={subunitLabel}
          />
        ))) ||
        null}
    </Fragment>
  );
};

const withLogSubjectVisit = graphql(logSubjectVisitMutation, {
  props: ({ mutate }) => ({
    logSubjectVisit: (path) =>
      mutate({
        variables: { path },
      }),
  }),
});

export default compose(
  withRouter,
  connect(
    ({
      userData,
      subject: { data: subjectData },
      visitedUnits,
      featureFlag,
      impersonating,
      isWelcomeVideoFirstTime,
      questionPreview,
    }) => ({
      userData,
      subjectData,
      visitedUnits,
      featureFlag,
      checkWizardOnLoadAndRedirect: checkWizardOnLoadAndRedirectAction,
      isImpersonating: impersonating.isReadOnly || isImpersonating(),
      isImpersonatingWelcomeVideoFirstTime:
        isWelcomeVideoFirstTime ||
        window.localStorage.getItem(
          LC_CONSTANTS.IS_WELCOME_VIDEO_FIRST_TIME,
        ) === 'true',
      dynamicItemsByUnit: questionPreview.dynamicItems,
    }),
  ),
  withCourseOutline(),
  AssignHelper,
  ShareHelper,
  withLogSubjectVisit,
)(withApollo(HomeContainer));
